import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { buildTelehealthSessionPath } from "@/routes";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  Users, 
  Clock, 
  Phone, 
  UserCheck, 
  AlertCircle,
  Loader2,
  RefreshCw,
  Volume2,
  VolumeX,
  Bell,
  BellOff
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useEffect } from "react";
import { useNotificationSound } from "@/hooks/useNotificationSound";
import { useBrowserNotification } from "@/hooks/useBrowserNotification";
import { ProviderChatPopover } from "./ProviderChatPopover";

interface WaitingSession {
  id: string;
  member_id: string;
  scheduled_date: string;
  scheduled_time: string;
  visit_type: string;
  visit_reason: string | null;
  status: string;
  created_at: string;
  memberName?: string;
  waitTime?: number;
}

export function WaitingMembersList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [admitDialogOpen, setAdmitDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<WaitingSession | null>(null);
  const previousSessionIdsRef = useRef<Set<string>>(new Set());
  
  // Notification hooks
  const { playSound, isEnabled: isSoundEnabled, toggleSound, initAudioContext } = useNotificationSound();
  const { permission, isSupported, requestPermission, showNotification, isGranted } = useBrowserNotification();

  // Initialize audio context on first interaction
  const handleEnableNotifications = useCallback(async () => {
    initAudioContext();
    if (isSupported && permission === "default") {
      await requestPermission();
    }
    // Play a test sound
    playSound("success");
    toast.success("Notifications enabled!");
  }, [initAudioContext, isSupported, permission, requestPermission, playSound]);

  // Fetch waiting sessions
  const { data: waitingSessions, isLoading, refetch } = useQuery({
    queryKey: ["waiting-sessions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("telehealth_sessions")
        .select("*")
        .eq("status", "waiting")
        .order("created_at", { ascending: true });

      if (error) throw error;

      // Fetch member names from profiles
      const sessionsWithNames = await Promise.all(
        (data || []).map(async (session) => {
          const { data: profile } = await supabase
            .from("profiles")
            .select("display_name, email")
            .eq("id", session.member_id)
            .single();

          // Also try intake form for name
          const { data: intake } = await supabase
            .from("patients")
            .select("first_name, last_name")
            .eq("user_id", session.member_id)
            .single();

          const memberName = intake 
            ? `${intake.first_name} ${intake.last_name}`
            : profile?.display_name || profile?.email?.split("@")[0] || "Unknown Member";

          return {
            ...session,
            memberName,
          };
        })
      );

      return sessionsWithNames as WaitingSession[];
    },
    refetchInterval: 10000, // Refetch every 10 seconds
  });

  // Subscribe to real-time updates for waiting sessions
  useEffect(() => {
    const channel = supabase
      .channel("waiting-sessions-realtime")
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "telehealth_sessions",
        },
        async (payload) => {
          // Check if someone just entered the waiting room
          if (payload.new.status === "waiting" && payload.old?.status !== "waiting") {
            // Fetch member name for notification
            const { data: intake } = await supabase
              .from("patients")
              .select("first_name, last_name")
              .eq("user_id", payload.new.member_id)
              .single();
            
            const { data: profile } = await supabase
              .from("profiles")
              .select("display_name, email")
              .eq("id", payload.new.member_id)
              .single();

            const memberName = intake 
              ? `${intake.first_name} ${intake.last_name}`
              : profile?.display_name || profile?.email?.split("@")[0] || "A member";

            // Play notification sound
            playSound("waiting");

            // Show browser notification
            showNotification("New Patient in Waiting Room", {
              body: `${memberName} is waiting for their telehealth session.`,
            });

            // Also show toast
            toast.info(`${memberName} has entered the waiting room`, {
              duration: 8000,
            });
          }
          
          // Refetch when any session changes
          queryClient.invalidateQueries({ queryKey: ["waiting-sessions"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient, playSound, showNotification]);

  // Admit member mutation
  const admitMutation = useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("telehealth_sessions")
        .update({ 
          status: "in_progress",
          started_at: new Date().toISOString()
        })
        .eq("id", sessionId);

      if (error) throw error;
      return sessionId;
    },
    onSuccess: (sessionId) => {
      toast.success("Member admitted to session");
      queryClient.invalidateQueries({ queryKey: ["waiting-sessions"] });
      setAdmitDialogOpen(false);
      setSelectedSession(null);
      navigate(buildTelehealthSessionPath(sessionId));
    },
    onError: (error) => {
      toast.error("Failed to admit member");
      console.error("Admit error:", error);
    },
  });

  const handleAdmitClick = (session: WaitingSession) => {
    setSelectedSession(session);
    setAdmitDialogOpen(true);
  };

  const handleConfirmAdmit = () => {
    if (selectedSession) {
      admitMutation.mutate(selectedSession.id);
    }
  };

  const calculateWaitTime = (createdAt: string) => {
    const created = new Date(createdAt);
    const now = new Date();
    const diffMs = now.getTime() - created.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    return diffMins;
  };

  const formatWaitTime = (minutes: number) => {
    if (minutes < 1) return "Just now";
    if (minutes === 1) return "1 min";
    if (minutes < 60) return `${minutes} mins`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  const waitingCount = waitingSessions?.length || 0;

  return (
    <>
      <Card className={waitingCount > 0 ? "border-green-500/50 bg-green-500/5" : ""}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`rounded-full p-2 ${waitingCount > 0 ? "bg-green-500/10 animate-pulse" : "bg-muted"}`}>
                <Users className={`h-5 w-5 ${waitingCount > 0 ? "text-green-500" : "text-muted-foreground"}`} />
              </div>
              <div>
                <CardTitle className="text-lg">Waiting Room</CardTitle>
                <CardDescription>
                  {waitingCount === 0 
                    ? "No members waiting" 
                    : `${waitingCount} member${waitingCount > 1 ? "s" : ""} waiting to be admitted`
                  }
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={toggleSound}
                      className={isSoundEnabled ? "text-primary" : "text-muted-foreground"}
                    >
                      {isSoundEnabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isSoundEnabled ? "Mute notification sounds" : "Enable notification sounds"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              
              {isSupported && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleEnableNotifications}
                        className={isGranted ? "text-primary" : "text-muted-foreground"}
                      >
                        {isGranted ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {isGranted ? "Browser notifications enabled" : "Enable browser notifications"}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              
              <Button variant="ghost" size="sm" onClick={() => refetch()}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {waitingCount === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">Members will appear here when they enter the waiting room</p>
            </div>
          ) : (
            <div className="space-y-3">
              {waitingSessions?.map((session) => {
                const waitMins = calculateWaitTime(session.created_at);
                const isLongWait = waitMins > 10;

                return (
                  <div
                    key={session.id}
                    className="flex items-center justify-between p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12 border-2 border-green-500">
                        <AvatarFallback className="bg-green-500/10 text-green-600 font-semibold">
                          {session.memberName?.split(" ").map(n => n[0]).join("").toUpperCase() || "?"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-foreground">{session.memberName}</p>
                        <div className="flex items-center gap-3 text-sm text-muted-foreground">
                          <span className="capitalize">{session.visit_type.replace("_", " ")}</span>
                          {session.visit_reason && (
                            <>
                              <span>•</span>
                              <span className="truncate max-w-[200px]">{session.visit_reason}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className={`flex items-center gap-1 text-sm ${isLongWait ? "text-amber-500" : "text-muted-foreground"}`}>
                          <Clock className="h-4 w-4" />
                          <span className="font-medium">{formatWaitTime(waitMins)}</span>
                        </div>
                        {isLongWait && (
                          <div className="flex items-center gap-1 text-xs text-amber-500">
                            <AlertCircle className="h-3 w-3" />
                            <span>Long wait</span>
                          </div>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        <ProviderChatPopover 
                          sessionId={session.id} 
                          memberName={session.memberName || "Member"} 
                        />
                        
                        <Button 
                          onClick={() => handleAdmitClick(session)}
                          className="gap-2"
                          disabled={admitMutation.isPending}
                        >
                          {admitMutation.isPending && selectedSession?.id === session.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <UserCheck className="h-4 w-4" />
                          )}
                          Admit
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm Admit Dialog */}
      <AlertDialog open={admitDialogOpen} onOpenChange={setAdmitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              Admit {selectedSession?.memberName}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will start the telehealth session. The member will be notified and brought into the video call immediately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={admitMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleConfirmAdmit}
              disabled={admitMutation.isPending}
              className="gap-2"
            >
              {admitMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Phone className="h-4 w-4" />
              )}
              Start Session
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
